import type { TenantAnalyticsCampaignParameters } from './types';

const MAX_ANALYTICS_PATH_LENGTH = 2_048;
const MAX_CAMPAIGN_PARAMETER_LENGTH = 256;
/** TikTok documents ttclid values of up to 1,000 characters. */
const MAX_SOCIAL_CLICK_ID_LENGTH = 1_000;
const ENCODED_CONTROL_CHARACTER = /%(?:0[0-9a-f]|1[0-9a-f]|7f)/i;
const URI_SCHEME = /^([a-z][a-z\d+.-]*):/i;
const CAMPAIGN_PARAMETER_NAMES = [
  'utm_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'ttclid',
  'ScCid',
] as const satisfies readonly (keyof TenantAnalyticsCampaignParameters)[];
const CAMPAIGN_PARAMETER_NAME_SET = new Set<string>(CAMPAIGN_PARAMETER_NAMES);
/**
 * Meta / TikTok / Snap ad-click ids. The pixels read them from the landing
 * URL for attribution, so a landing page carrying one must stay deliverable.
 */
const SOCIAL_CLICK_ID_PARAMETER_NAMES = new Set<string>([
  'fbclid',
  'ttclid',
  'ScCid',
]);

const SENSITIVE_ROUTE_PATTERNS = [
  // The path segment is a bearer credential: possession grants access to the
  // public guest pass. Do not report even a templated page view for it.
  /^\/pass(?:\/|$)/,
] as const;

const TENANT_ROUTE_TEMPLATES = [
  [/^\/class-categories\/[^/]+\/?$/, '/class-categories/[id]'],
  [/^\/coaches\/[^/]+\/?$/, '/coaches/[coachSlug]'],
  [/^\/event\/[^/]+\/?$/, '/event/[bookingId]'],
  [/^\/guests\/[^/]+\/?$/, '/guests/[passId]'],
  [/^\/memberships\/[^/]+\/guests\/?$/, '/memberships/[id]/guests'],
  [/^\/memberships\/[^/]+\/visits\/?$/, '/memberships/[id]/visits'],
  [/^\/memberships\/[^/]+\/?$/, '/memberships/[id]'],
  [/^\/payment\/guest\/[^/]+\/?$/, '/payment/guest/[passId]'],
  [/^\/payment\/membership\/[^/]+\/?$/, '/payment/membership/[membershipId]'],
  [/^\/payment\/order\/[^/]+\/?$/, '/payment/order/[id]'],
  [/^\/payment\/package\/[^/]+\/?$/, '/payment/package/[packageId]'],
  [/^\/payment\/[^/]+\/?$/, '/payment/[bookingId]'],
] as const satisfies readonly (readonly [RegExp, string])[];

interface SanitizedTenantAnalyticsPageLocation {
  readonly path: string;
  readonly campaign: TenantAnalyticsCampaignParameters;
}

/**
 * Produces a privacy-safe route path for analytics.
 *
 * Fragments and arbitrary query values are removed. Only the bounded campaign
 * and ad-click identifiers in `TenantAnalyticsCampaignParameters` survive,
 * serialized in a fixed order. HTTP(S) URLs are reduced to this safe path;
 * other schemes and protocol-relative URLs are rejected.
 */
export function sanitizeTenantAnalyticsPath(input: string): string | null {
  return sanitizePageLocation(input)?.path ?? null;
}

/**
 * Whether a browser pixel may safely derive its page URL and referrer from the
 * current document. Pixel SDKs attach those values outside our event payload,
 * so canonicalization alone cannot protect a dynamic or query-bearing URL.
 */
export function isTenantAnalyticsDocumentLocationSafe(
  input: string,
  referrer = '',
) {
  const parsed = parseHttpLocation(input);

  if (
    !parsed ||
    parsed.hash.length > 0 ||
    !hasOnlySafeCampaignParameters(parsed.searchParams) ||
    canonicalizeTenantPathname(parsed.pathname) !== parsed.pathname
  ) {
    return false;
  }

  if (!referrer) {
    return true;
  }

  const parsedReferrer = parseAbsoluteHttpLocation(referrer);

  return (
    parsedReferrer !== null &&
    parsedReferrer.search.length === 0 &&
    parsedReferrer.hash.length === 0 &&
    canonicalizeTenantPathname(parsedReferrer.pathname) ===
      parsedReferrer.pathname
  );
}

/** Returns the bounded campaign values retained by the path sanitizer. */
export function extractTenantAnalyticsCampaignParameters(
  input: string,
): TenantAnalyticsCampaignParameters | null {
  return sanitizePageLocation(input)?.campaign ?? null;
}

/**
 * Retains attribution from an initial browser referrer without retaining its
 * query, fragment, or credentials. Relative and non-HTTP(S) referrers are
 * rejected because they cannot identify an external origin safely.
 */
export function sanitizeTenantAnalyticsReferrer(input: string): string | null {
  const value = normalizeInput(input);
  const scheme = value?.match(URI_SCHEME)?.[1]?.toLowerCase();

  if (!value || (scheme !== 'http' && scheme !== 'https')) {
    return null;
  }

  try {
    const parsed = new URL(value);

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }

    const pathname = canonicalizeTenantPathname(parsed.pathname || '/');

    if (!pathname || !isSafePathname(pathname)) {
      return null;
    }

    const referrer = `${parsed.origin}${pathname}`;

    return referrer.length <= MAX_ANALYTICS_PATH_LENGTH ? referrer : null;
  } catch {
    return null;
  }
}

function sanitizePageLocation(
  input: string,
): SanitizedTenantAnalyticsPageLocation | null {
  const value = normalizeInput(input);

  if (!value || value.startsWith('//')) {
    return null;
  }

  const scheme = value.match(URI_SCHEME)?.[1]?.toLowerCase();

  if (scheme && scheme !== 'http' && scheme !== 'https') {
    return null;
  }

  try {
    const parsed = new URL(value, 'https://tenant-analytics.invalid');

    if (
      (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') ||
      parsed.username.length > 0 ||
      parsed.password.length > 0
    ) {
      return null;
    }

    const pathname = canonicalizeTenantPathname(parsed.pathname || '/');

    if (!pathname || !isSafePathname(pathname)) {
      return null;
    }

    const campaign = readCampaignParameters(parsed.searchParams);
    const search = createCampaignSearch(campaign);
    const path = search ? `${pathname}?${search}` : pathname;

    if (path.length > MAX_ANALYTICS_PATH_LENGTH) {
      return null;
    }

    return Object.freeze({ path, campaign });
  } catch {
    return null;
  }
}

function canonicalizeTenantPathname(pathname: string) {
  if (
    !isSafePathname(pathname) ||
    SENSITIVE_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))
  ) {
    return null;
  }

  for (const [pattern, template] of TENANT_ROUTE_TEMPLATES) {
    if (pattern.test(pathname)) {
      return template;
    }
  }

  return pathname;
}

function hasOnlySafeCampaignParameters(searchParams: URLSearchParams) {
  const seen = new Set<string>();

  for (const [name, value] of searchParams) {
    if (
      !CAMPAIGN_PARAMETER_NAME_SET.has(name) ||
      seen.has(name) ||
      normalizeCampaignValue(name, value) === null
    ) {
      return false;
    }

    seen.add(name);
  }

  return true;
}

function parseHttpLocation(input: string) {
  const value = normalizeInput(input);

  if (!value || value.startsWith('//')) {
    return null;
  }

  try {
    const parsed = new URL(value, 'https://tenant-analytics.invalid');

    return isSafeHttpUrl(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function parseAbsoluteHttpLocation(input: string) {
  const value = normalizeInput(input);
  const scheme = value?.match(URI_SCHEME)?.[1]?.toLowerCase();

  if (!value || (scheme !== 'http' && scheme !== 'https')) {
    return null;
  }

  try {
    const parsed = new URL(value);

    return isSafeHttpUrl(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function isSafeHttpUrl(parsed: URL) {
  return (
    (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
    parsed.username.length === 0 &&
    parsed.password.length === 0 &&
    isSafePathname(parsed.pathname || '/')
  );
}

function readCampaignParameters(searchParams: URLSearchParams) {
  const entries: [keyof TenantAnalyticsCampaignParameters, string][] = [];

  for (const name of CAMPAIGN_PARAMETER_NAMES) {
    const value = normalizeCampaignValue(name, searchParams.get(name));

    if (value) {
      entries.push([name, value]);
    }
  }

  return Object.freeze(
    Object.fromEntries(entries),
  ) as TenantAnalyticsCampaignParameters;
}

function createCampaignSearch(campaign: TenantAnalyticsCampaignParameters) {
  const search = new URLSearchParams();

  for (const name of CAMPAIGN_PARAMETER_NAMES) {
    const value = campaign[name];

    if (value) {
      search.set(name, value);
    }
  }

  return search.toString();
}

function normalizeCampaignValue(name: string, value: string | null) {
  if (value === null) {
    return null;
  }

  const normalized = value.trim();
  const maximumLength = SOCIAL_CLICK_ID_PARAMETER_NAMES.has(name)
    ? MAX_SOCIAL_CLICK_ID_LENGTH
    : MAX_CAMPAIGN_PARAMETER_LENGTH;

  return normalized.length > 0 &&
    normalized.length <= maximumLength &&
    !hasControlCharacter(normalized)
    ? normalized
    : null;
}

function normalizeInput(input: string) {
  const value = input.trim();

  return value.length > 0 &&
    value.length <= MAX_ANALYTICS_PATH_LENGTH &&
    !value.includes('\\') &&
    !hasControlCharacter(value) &&
    !ENCODED_CONTROL_CHARACTER.test(value)
    ? value
    : null;
}

function isSafePathname(pathname: string) {
  return (
    pathname.length <= MAX_ANALYTICS_PATH_LENGTH &&
    !hasControlCharacter(pathname) &&
    !ENCODED_CONTROL_CHARACTER.test(pathname)
  );
}

function hasControlCharacter(value: string) {
  for (let index = 0; index < value.length; index += 1) {
    const characterCode = value.charCodeAt(index);

    if (characterCode <= 31 || characterCode === 127) {
      return true;
    }
  }

  return false;
}
